import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Phone, Video, MessageCircle, CheckCircle, XCircle, Clock as ClockIcon, Star, MapPin, AlertTriangle, ThumbsUp, Trash2, CheckSquare, Square, User } from 'lucide-react';
import RatingModal from './RatingModal';
import RatingReminder from './RatingReminder';
import toast from 'react-hot-toast';
import { bookingService } from '../services/bookingService';
import dataSyncService from '../services/dataSyncService';

const MyBookings = () => {
  const navigate = useNavigate();
  const { language, setLawyers, addActivity, user } = useApp();
  const [activeTab, setActiveTab] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showRatingReminder, setShowRatingReminder] = useState(false);
  const [recentlyCompletedBooking, setRecentlyCompletedBooking] = useState(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [realTimeUnsubscribe, setRealTimeUnsubscribe] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);


  const translations = {
    english: {
      title: 'My Bookings',
      subtitle: 'Manage your legal consultations',
      all: 'All',
      upcoming: 'Upcoming',
      ongoing: 'Ongoing',
      completed: 'Completed',
      rescheduled: 'Rescheduled',
      confirmed: 'Confirmed',
      inProgress: 'In Progress',
      cancelled: 'Cancelled',
      chat: 'Chat',
      call: 'Call',
      videoCall: 'Video Call',
      reschedule: 'Reschedule',
      joinCall: 'Join Call',
      viewProfile: 'View Profile',
      bookingId: 'Booking ID',
      consultationType: 'Consultation Type',
      dateTime: 'Date & Time',
      fee: 'Fee',
      status: 'Status',
      noBookings: 'No bookings found',
      noBookingsDesc: 'You haven\'t made any bookings yet',
      completeBooking: 'Complete Booking',
      cancelBooking: 'Cancel Booking',
      deleteBooking: 'Delete Booking',
      deleteSelected: 'Delete Selected',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      originalDateTime: 'Original Date & Time',
      newDateTime: 'New Date & Time',
      rescheduledBy: 'Rescheduled by',
      rescheduledAt: 'Rescheduled at',
      rateAndReview: 'Rate & Review',
      alreadyRated: 'Already Rated',
      ratedOn: 'Rated on',
      rateExperience: 'Rate Your Experience',
      submitFeedback: 'Submit Feedback',
      bookingCompleted: 'Booking completed',
      bookingCancelled: 'Booking cancelled',
      bookingDeleted: 'Booking deleted',
      lawyerNotified: 'Lawyer has been notified',
      confirmDelete: 'Confirm Delete',
      confirmDeleteMessage: 'Are you sure you want to delete this booking? This action cannot be undone.',
      confirmBulkDelete: 'Confirm Bulk Delete',
      confirmBulkDeleteMessage: 'Are you sure you want to delete {count} selected bookings? This action cannot be undone.',
      deleteSuccess: 'Booking deleted successfully',
      bulkDeleteSuccess: '{count} bookings deleted successfully',
      deleteError: 'Failed to delete booking',
      bulkDeleteError: 'Failed to delete some bookings'
    },
    hindi: {
      title: 'मेरी बुकिंग',
      subtitle: 'अपने कानूनी परामर्श का प्रबंधन करें',
      all: 'सभी',
      upcoming: 'आगामी',
      ongoing: 'चल रहा',
      completed: 'पूर्ण',
      rescheduled: 'पुनर्निर्धारित',
      confirmed: 'पुष्टि की गई',
      inProgress: 'प्रगति में',
      cancelled: 'रद्द',
      chat: 'चैट',
      call: 'कॉल',
      videoCall: 'वीडियो कॉल',
      reschedule: 'पुनर्निर्धारण',
      joinCall: 'कॉल में शामिल हों',
      viewProfile: 'प्रोफ़ाइल देखें',
      bookingId: 'बुकिंग आईडी',
      consultationType: 'परामर्श प्रकार',
      dateTime: 'दिनांक और समय',
      fee: 'शुल्क',
      status: 'स्थिति',
      noBookings: 'कोई बुकिंग नहीं मिली',
      noBookingsDesc: 'आपने अभी तक कोई बुकिंग नहीं की है',
      completeBooking: 'बुकिंग पूरी करें',
      cancelBooking: 'बुकिंग रद्द करें',
      deleteBooking: 'बुकिंग हटाएं',
      deleteSelected: 'चयनित हटाएं',
      selectAll: 'सभी चुनें',
      deselectAll: 'सभी अचयनित करें',
      originalDateTime: 'मूल दिनांक और समय',
      newDateTime: 'नया दिनांक और समय',
      rescheduledBy: 'द्वारा पुनर्निर्धारित',
      rescheduledAt: 'पुनर्निर्धारित किया गया',
      rateAndReview: 'रेटिंग और समीक्षा',
      alreadyRated: 'पहले से रेट किया गया',
      ratedOn: 'रेट किया गया',
      rateExperience: 'अपने अनुभव को रेट करें',
      submitFeedback: 'प्रतिक्रिया सबमिट करें',
      bookingCompleted: 'बुकिंग पूरी हुई',
      bookingCancelled: 'बुकिंग रद्द की गई',
      bookingDeleted: 'बुकिंग हटा दी गई',
      lawyerNotified: 'वकील को सूचित किया गया है',
      confirmDelete: 'हटाने की पुष्टि करें',
      confirmDeleteMessage: 'क्या आप वाकई इस बुकिंग को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।',
      confirmBulkDelete: 'बल्क हटाने की पुष्टि करें',
      confirmBulkDeleteMessage: 'क्या आप वाकई {count} चयनित बुकिंग को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।',
      deleteSuccess: 'बुकिंग सफलतापूर्वक हटा दी गई',
      bulkDeleteSuccess: '{count} बुकिंग सफलतापूर्वक हटा दी गईं',
      deleteError: 'बुकिंग हटाने में विफल',
      bulkDeleteError: 'कुछ बुकिंग हटाने में विफल'
    }
  };

  const t = translations[language] || translations.english;

  const tabs = [
    { id: 'all', label: t.all },
    { id: 'ongoing', label: t.ongoing },
    { id: 'rescheduled', label: t.rescheduled },
    { id: 'completed', label: t.completed }
  ];

  // Helper function to get current user ID and localStorage keys
  const getUserStorageKeys = () => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const userId = currentUser.uid || currentUser.id || 'anonymous';
    return {
      userId,
      userBookingsKey: `userBookings_${userId}`,
      generalBookingsKey: `bookings_${userId}`
    };
  };

  // Load bookings from localStorage and Firestore with enhanced persistence
  useEffect(() => {
    const loadBookings = async () => {
      try {
        setLoading(true);
        const { userId, userBookingsKey, generalBookingsKey } = getUserStorageKeys();
        
        console.log('🔄 Loading bookings for user:', userId);
        
        // ALWAYS load from localStorage first for immediate display (offline-first approach)
        const storedBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
        const oldUserBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        const oldGeneralBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
        
        // Also check for any other booking-related keys
        const allKeys = Object.keys(localStorage);
        const otherBookingKeys = allKeys.filter(key => 
          key.includes('booking') || key.includes('appointment') || key.includes('consultation') ||
          key.includes('case') || key.includes('legal')
        );
        
        let additionalBookings = [];
        otherBookingKeys.forEach(key => {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(data) && data.length > 0) {
              additionalBookings.push(...data);
            }
          } catch (e) {
            console.log('⚠️ Could not parse data from key:', key);
          }
        });
        
        // Merge all localStorage sources
        let allLocalBookings = [
          ...storedBookings, 
          ...generalBookings, 
          ...oldUserBookings, 
          ...oldGeneralBookings,
          ...additionalBookings
        ];
        
        // Remove duplicates based on ID
        allLocalBookings = allLocalBookings.filter((booking, index, self) => 
          index === self.findIndex(b => b.id === booking.id)
        );
        
        console.log('🔍 Found bookings from all sources:', {
          storedBookings: storedBookings.length,
          generalBookings: generalBookings.length,
          oldUserBookings: oldUserBookings.length,
          oldGeneralBookings: oldGeneralBookings.length,
          additionalBookings: additionalBookings.length,
          total: allLocalBookings.length
        });
        
        // Set bookings immediately from localStorage for instant display
        if (allLocalBookings.length > 0) {
          setBookings(allLocalBookings);
          console.log('📱 Set bookings from localStorage:', allLocalBookings.length);
        }
        
        // Then try to sync with Firestore if user is authenticated and online (in background)
        if (user?.uid && navigator.onLine) {
          // Use setTimeout to not block the UI
          setTimeout(async () => {
            try {
              console.log('🌐 Attempting to sync with Firestore...');
              const result = await bookingService.getClientBookings(user.uid);
              
              if (result.success && result.data) {
                const firestoreBookings = result.data;
                console.log('✅ Successfully loaded bookings from Firestore:', firestoreBookings.length);
                
                // Merge Firestore data with localStorage data
                const mergedBookings = [...firestoreBookings];
                
                // Add localStorage bookings that aren't in Firestore
                allLocalBookings.forEach(localBooking => {
                  const exists = mergedBookings.find(b => b.id === localBooking.id);
                  if (!exists) {
                    mergedBookings.push(localBooking);
                  } else {
                    // Update localStorage booking with Firestore data if it's more recent
                    const firestoreBooking = mergedBookings.find(b => b.id === localBooking.id);
                    if (firestoreBooking && firestoreBooking.updatedAt > localBooking.updatedAt) {
                      const index = mergedBookings.findIndex(b => b.id === localBooking.id);
                      mergedBookings[index] = { ...localBooking, ...firestoreBooking };
                    }
                  }
                });
                
                // Update state and localStorage
                setBookings(mergedBookings);
                localStorage.setItem(userBookingsKey, JSON.stringify(mergedBookings));
                localStorage.setItem(generalBookingsKey, JSON.stringify(mergedBookings));
                
                console.log('💾 Saved merged bookings to localStorage with multiple keys');
              } else {
                console.log('⚠️ No Firestore data, using localStorage data');
                // If Firestore has no data, still show localStorage data
                if (allLocalBookings.length > 0) {
                  setBookings(allLocalBookings);
                }
              }
            } catch (firestoreError) {
              console.error('⚠️ Error syncing with Firestore:', firestoreError);
              console.log('📱 Continuing with localStorage data only');
              // If Firestore fails, still show localStorage data
              if (allLocalBookings.length > 0) {
                setBookings(allLocalBookings);
              }
            }
          }, 100); // Small delay to not block UI
        } else {
          console.log('📱 Offline mode or no user - using localStorage data only');
          // Always show localStorage data even when offline
          if (allLocalBookings.length > 0) {
            setBookings(allLocalBookings);
          }
        }
        
        // Clean up old storage keys if we have user-specific data
        if (userId !== 'anonymous' && allLocalBookings.length > 0) {
          localStorage.removeItem('userBookings');
          localStorage.removeItem('bookings');
          console.log('🧹 Cleaned up old global storage keys');
        }
        
        console.log(`📊 Final booking count: ${allLocalBookings.length}`);
        
      } catch (error) {
        console.error('❌ Error loading bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    loadBookings();
  }, [user?.uid]);

  // Function to refresh bookings from Firestore
  const refreshBookings = async () => {
    try {
      setRefreshing(true);
      if (!user?.uid) {
        toast.error('Please log in to refresh bookings');
        return;
      }
      
      console.log('🔄 Refreshing bookings from Firestore...');
      
      // Use the enhanced sync service for better data consistency
      if (dataSyncService && typeof dataSyncService.syncClientBookings === 'function') {
        const syncResult = await dataSyncService.syncClientBookings(user.uid);
        if (syncResult.success && syncResult.data) {
          setBookings(syncResult.data);
          toast.success(`✅ Bookings refreshed successfully! Found ${syncResult.data.length} bookings`);
          console.log('✅ Refreshed bookings via sync service:', syncResult.data.length);
        } else {
          // Fallback to direct service
          console.log('⚠️ Sync service failed, trying direct service...');
          const result = await bookingService.getClientBookings(user.uid);
          
          if (result.success && result.data) {
            setBookings(result.data);
            toast.success(`✅ Bookings refreshed successfully! Found ${result.data.length} bookings`);
            console.log('✅ Refreshed bookings via direct service:', result.data.length);
          } else {
            toast.error('Failed to refresh bookings');
          }
        }
      } else {
        // Fallback to direct service if sync service not available
        console.log('⚠️ Sync service not available, using direct service...');
        const result = await bookingService.getClientBookings(user.uid);
        
        if (result.success && result.data) {
          setBookings(result.data);
          toast.success(`✅ Bookings refreshed successfully! Found ${result.data.length} bookings`);
          console.log('✅ Refreshed bookings via direct service:', result.data.length);
        } else {
          toast.error('Failed to refresh bookings');
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing bookings:', error);
      toast.error('Error refreshing bookings');
      
      // Try to load from localStorage as last resort
      try {
        const { userBookingsKey } = getUserStorageKeys();
        const localBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        if (localBookings.length > 0) {
          setBookings(localBookings);
          toast.info(`Loaded ${localBookings.length} bookings from local storage`);
        }
      } catch (localError) {
        console.error('❌ Failed to load from localStorage:', localError);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Function to manually sync localStorage bookings to Firestore
  const syncLocalBookingsToFirestore = async () => {
    try {
      if (!user?.uid) {
        toast.error('Please log in to sync bookings');
        return;
      }
      
      const { userBookingsKey } = getUserStorageKeys();
      const localBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
      
      if (localBookings.length === 0) {
        toast.info('No local bookings to sync');
        return;
      }
      
      console.log('🔄 Syncing local bookings to Firestore:', localBookings.length);
      
      let syncedCount = 0;
      for (const booking of localBookings) {
        try {
          const bookingData = {
            userId: user.uid,
            clientName: booking.clientName || booking.userName || 'Client',
            clientEmail: booking.clientError || booking.userEmail || user.email,
            lawyerId: booking.lawyerId || booking.lawyer_id,
            lawyerName: booking.lawyerName || booking.lawyer?.name || 'Lawyer',
            lawyerEmail: booking.lawyerEmail || booking.lawyer?.email || '',
            appointmentDate: booking.appointmentDate || booking.date,
            appointmentTime: booking.appointmentTime || booking.time,
            consultationType: booking.consultationType || booking.type || 'general',
            totalAmount: booking.totalAmount || booking.amount || 0,
            status: booking.status || 'pending'
          };
          
          const result = await bookingService.createBooking(bookingData);
          if (result.success) {
            syncedCount++;
          }
        } catch (error) {
          console.error('Error syncing booking:', booking.id, error);
        }
      }
      
      if (syncedCount > 0) {
        toast.success(`✅ Synced ${syncedCount} bookings to Firestore`);
        // Refresh the bookings list
        await refreshBookings();
      } else {
        toast.info('No new bookings to sync');
      }
    } catch (error) {
      console.error('❌ Error syncing bookings:', error);
      toast.error('Error syncing bookings');
    }
  };


  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
      case 'upcoming':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'ongoing':
      case 'in progress':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'completed':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'rescheduled':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'cancelled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getConsultationTypeIcon = (type) => {
    if (!type || typeof type !== 'string') {
      return <MessageCircle className="w-4 h-4" />;
    }
    
    switch (type.toLowerCase()) {
      case 'chat':
      case 'text':
        return <MessageCircle className="w-4 h-4" />;
      case 'call':
        return <Phone className="w-4 h-4" />;
      case 'video call':
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'inperson':
      case 'in-person':
        return <User className="w-4 h-4" />;
      case 'emergency':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getConsultationTypeName = (type) => {
    if (!type || typeof type !== 'string') {
      return 'Consultation';
    }
    
    switch (type.toLowerCase()) {
      case 'chat':
      case 'text':
        return 'Text Consultation';
      case 'call':
        return 'Phone Call';
      case 'video call':
      case 'video':
        return 'Video Call';
      case 'inperson':
      case 'in-person':
        return 'In-Person';
      case 'emergency':
        return 'Emergency';
      default:
        return type;
    }
  };

  const formatBookingDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return date;
    }
  };

  const formatBookingTime = (time) => {
    if (!time) return '';
    try {
      // If time is already in 12-hour format, return as is
      if (time.includes('AM') || time.includes('PM')) {
        return time;
      }
      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (error) {
      return time;
    }
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
    if (activeTab === 'all') return true;
    return booking.status.toLowerCase() === activeTab;
  });
  }, [bookings, activeTab]);

  const handleAction = async (action, booking) => {
    switch (action) {
      case 'reschedule':
        // Handle reschedule
        console.log('Reschedule booking:', booking.id);
        break;
      case 'joinCall':
        // Handle join call
        console.log('Join call for booking:', booking.id);
        break;
      case 'viewProfile':
        // Navigate to lawyer detail page with full lawyer data
        console.log('View Profile clicked for booking:', booking);
        console.log('🔍 Booking data structure:', {
          id: booking.id,
          lawyerId: booking.lawyerId,
          lawyer_id: booking.lawyer_id,
          lawyerName: booking.lawyerName,
          lawyer: booking.lawyer,
          lawyerEmail: booking.lawyerEmail,
          specialization: booking.specialization,
          location: booking.location,
          rating: booking.rating,
          experience: booking.experience
        });
        
        // Try multiple approaches to get lawyer ID
        let lawyerId = null;
        let lawyerData = null;
        
        // Approach 1: Check if booking has complete lawyer object (highest priority)
        if (booking.lawyer && booking.lawyer.id) {
          lawyerId = booking.lawyer.id;
          lawyerData = booking.lawyer;
          console.log('✅ Found complete lawyer data in booking:', lawyerData);
        }
        // Approach 2: Check if booking has lawyerId
        else if (booking.lawyerId) {
          lawyerId = booking.lawyerId;
        }
        // Approach 3: Check if we can find lawyer in available lawyers list
        else {
          const availableLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
          const foundLawyer = availableLawyers.find(l => 
            l.id === booking.lawyerId || 
            l.full_name === booking.lawyerName ||
            l.name === booking.lawyerName
          );
          if (foundLawyer) {
            lawyerId = foundLawyer.id;
            lawyerData = foundLawyer;
            console.log('✅ Found lawyer in available lawyers list:', foundLawyer);
          }
        }
        
        if (!lawyerId) {
          console.error('No lawyer ID found in booking:', booking);
          toast.error('Lawyer information not available. Please try again.');
          return;
        }
        
        // If we don't have lawyerData, create it from booking data
        if (!lawyerData) {
          // First try to get complete lawyer data from available lawyers
          const availableLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
          console.log('🔍 Available lawyers count:', availableLawyers.length);
          console.log('🔍 Looking for lawyer ID:', lawyerId);
          console.log('🔍 Available lawyer IDs:', availableLawyers.map(l => l.id));
          
          // Try exact ID match first
          let completeLawyer = availableLawyers.find(l => l.id === lawyerId);
          
          // If not found, try to find by name
          if (!completeLawyer) {
            completeLawyer = availableLawyers.find(l => 
              l.full_name === booking.lawyerName ||
              l.name === booking.lawyerName ||
              l.full_name?.toLowerCase() === booking.lawyerName?.toLowerCase() ||
              l.name?.toLowerCase() === booking.lawyerName?.toLowerCase()
            );
            console.log('🔍 Found lawyer by name:', completeLawyer);
          }
          
          if (completeLawyer) {
            // Use the complete lawyer data from available lawyers
            lawyerData = {
              ...completeLawyer,
              // Override with booking-specific data if available
              bar_council_number: booking.barLicense || completeLawyer.bar_council_number || completeLawyer.barCouncilLicense || completeLawyer.barLicense,
              chamber_address: booking.chamberAddress || completeLawyer.chamber_address || completeLawyer.chamberAddress || completeLawyer.address,
              fees: {
                text: booking.textFee || completeLawyer.fees?.text || 1500,
                call: booking.callFee || completeLawyer.fees?.call || 2500,
                video: booking.videoFee || completeLawyer.fees?.video || 3000,
                in_person: booking.inPersonFee || completeLawyer.fees?.in_person || 3500,
                emergency: booking.emergencyFee || completeLawyer.fees?.emergency || 5000,
                consultation: booking.consultationFees || completeLawyer.fees?.consultation || 2500
              }
            };
            console.log('✅ Using complete lawyer data from available lawyers:', lawyerData);
          } else {
            // Try to get from Firebase as fallback
            console.log('🔍 Trying to load lawyer from Firebase...');
            try {
              const { collection, getDoc, doc } = await import('firebase/firestore');
              const { db } = await import('../config/firebase');
              
              // Use the lawyerId directly as the document ID
              const lawyerDocRef = doc(db, 'lawyers', lawyerId);
              const lawyerDoc = await getDoc(lawyerDocRef);
              
              if (lawyerDoc.exists()) {
                const firebaseLawyer = { id: lawyerDoc.id, ...lawyerDoc.data() };
                console.log('🔍 Raw Firebase lawyer data:', firebaseLawyer);
                console.log('🔍 BAR License from Firebase:', firebaseLawyer.bar_council_number);
                console.log('🔍 Chamber Address from Firebase:', firebaseLawyer.chamber_address);
                console.log('🔍 Consultation Fee from Firebase:', firebaseLawyer.fees?.consultation);
                
                lawyerData = {
                  ...firebaseLawyer,
                  bar_council_number: booking.barLicense || firebaseLawyer.bar_council_number || firebaseLawyer.barCouncilLicense || firebaseLawyer.barLicense,
                  chamber_address: booking.chamberAddress || firebaseLawyer.chamber_address || firebaseLawyer.chamberAddress || firebaseLawyer.address,
                  fees: {
                    text: booking.textFee || firebaseLawyer.fees?.text || 1500,
                    call: booking.callFee || firebaseLawyer.fees?.call || 2500,
                    video: booking.videoFee || firebaseLawyer.fees?.video || 3000,
                    in_person: booking.inPersonFee || firebaseLawyer.fees?.in_person || 3500,
                    emergency: booking.emergencyFee || firebaseLawyer.fees?.emergency || 5000,
                    consultation: booking.consultationFees || firebaseLawyer.fees?.consultation || 2500
                  }
                };
                console.log('✅ Using lawyer data from Firebase:', lawyerData);
              } else {
                console.log('⚠️ Lawyer not found in Firebase');
              }
            } catch (error) {
              console.log('⚠️ Could not load from Firebase:', error);
            }
            
            // If still no lawyer data, show error and don't proceed
            if (!lawyerData) {
              console.error('❌ No lawyer data found from any source for lawyer ID:', lawyerId);
              toast.error('Lawyer data not available. Please contact support.');
              return;
            }
        }
        }
        
        // Use the lawyer data as is - no fallback values
        const validatedLawyerData = lawyerData;
        
        // Check if we have complete data
        if (!lawyerData.achievements || lawyerData.achievements.length === 0) {
          console.warn('⚠️ Lawyer data may be incomplete - missing achievements');
        }
        
        console.log('✅ Final validated lawyer data:', validatedLawyerData);
        console.log('📋 Navigation state being passed:', {
          lawyer: validatedLawyerData,
          fromBooking: true,
          bookingData: booking,
          existingBooking: booking
        });
        
        try {
          // Try navigation with state - include booking information
          navigate(`/lawyer-detail/${lawyerId}`, {
            state: { 
              lawyer: validatedLawyerData,
              fromBooking: true,
              bookingData: booking,
              existingBooking: booking
            }
          });
        } catch (error) {
          console.error('Navigation error:', error);
          
          // Fallback: try navigation without state
          try {
            navigate(`/lawyer-detail/${lawyerId}`);
          } catch (fallbackError) {
            console.error('Fallback navigation also failed:', fallbackError);
            toast.error('Failed to open lawyer profile. Please try again.');
          }
        }
        break;
      case 'chat':
        // Find the chat for this booking
        const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
        const chat = storedChats.find(c => c.bookingId === booking.id);
        
        if (chat) {
          console.log('✅ Found existing chat for booking:', chat);
          navigate(`/chat-conversation/${chat.id}`, { state: { chat } });
        } else {
          console.log('❌ No chat found for booking, redirecting to booking details');
          // If no chat exists, show booking details instead
          navigate(`/booking-detail/${booking.id}`);
        }
        break;
      case 'complete':
        completeBooking(booking.id);
        break;
      case 'cancel':
        cancelBooking(booking.id);
        break;
      case 'delete':
        deleteBooking(booking.id);
        break;
      default:
        break;
    }
  };

  // Selection mode functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedBookings([]);
    }
  };

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  const selectAllBookings = () => {
    setSelectedBookings(filteredBookings.map(booking => booking.id));
  };

  const deselectAllBookings = () => {
    setSelectedBookings([]);
  };

  // Delete functions
  const deleteBooking = async (bookingId) => {
    if (!window.confirm(t.confirmDeleteMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      const { userId, userBookingsKey, generalBookingsKey } = getUserStorageKeys();
      
      // Try to delete from Firebase first
      const result = await bookingService.deleteBooking(bookingId);
      
      // Remove from local state regardless of Firebase result
      const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
      setBookings(updatedBookings);
      
      // Update localStorage with user-specific keys
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
      
      // Also remove from general bookings
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const updatedGeneralBookings = generalBookings.filter(booking => booking.id !== bookingId);
      localStorage.setItem(generalBookingsKey, JSON.stringify(updatedGeneralBookings));
      
      if (result.success) {
        toast.success(t.deleteSuccess);
      } else {
        // If Firebase failed, still show success for local deletion
        toast.success('Booking deleted locally');
      }
      
      // Add activity for booking deletion
      addActivity({
        type: 'booking_deleted',
        title: t.bookingDeleted,
        description: 'Booking has been permanently deleted',
        icon: 'trash',
        priority: 'medium'
      });
    } catch (error) {
      console.error('Error deleting booking:', error);
      // Even if there's an error, remove from local state
      const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
      setBookings(updatedBookings);
      
      const { userId, userBookingsKey, generalBookingsKey } = getUserStorageKeys();
      
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
      
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const updatedGeneralBookings = generalBookings.filter(booking => booking.id !== bookingId);
      localStorage.setItem(generalBookingsKey, JSON.stringify(updatedGeneralBookings));
      
      toast.success('Booking deleted locally');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteSelectedBookings = async () => {
    if (selectedBookings.length === 0) {
      toast.error('No bookings selected');
      return;
    }

    const confirmMessage = t.confirmBulkDeleteMessage.replace('{count}', selectedBookings.length);
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    try {
      const { userId, userBookingsKey, generalBookingsKey } = getUserStorageKeys();
      
      // Try to delete from Firebase first
      const result = await bookingService.deleteMultipleBookings(selectedBookings);
      
      // Remove from local state regardless of Firebase result
      const updatedBookings = bookings.filter(booking => !selectedBookings.includes(booking.id));
      setBookings(updatedBookings);
      
      // Update localStorage with user-specific keys
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
      
      // Also remove from general bookings
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const updatedGeneralBookings = generalBookings.filter(booking => !selectedBookings.includes(booking.id));
      localStorage.setItem(generalBookingsKey, JSON.stringify(updatedGeneralBookings));
      
      if (result.success) {
        const successMessage = t.bulkDeleteSuccess.replace('{count}', selectedBookings.length);
        toast.success(successMessage);
      } else {
        // If Firebase failed, still show success for local deletion
        toast.success(`${selectedBookings.length} bookings deleted locally`);
      }
      
      // Add activity for bulk deletion
      addActivity({
        type: 'bookings_deleted',
        title: 'Multiple bookings deleted',
        description: `${selectedBookings.length} bookings have been permanently deleted`,
        icon: 'trash',
        priority: 'medium'
      });
      
      // Exit selection mode
      setIsSelectionMode(false);
      setSelectedBookings([]);
    } catch (error) {
      console.error('Error deleting bookings:', error);
      // Even if there's an error, remove from local state
      const updatedBookings = bookings.filter(booking => !selectedBookings.includes(booking.id));
      setBookings(updatedBookings);
      
      const { userId, userBookingsKey, generalBookingsKey } = getUserStorageKeys();
      
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
      
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const updatedGeneralBookings = generalBookings.filter(booking => !selectedBookings.includes(booking.id));
      localStorage.setItem(generalBookingsKey, JSON.stringify(updatedGeneralBookings));
      
      toast.success(`${selectedBookings.length} bookings deleted locally`);
      
      // Exit selection mode
      setIsSelectionMode(false);
      setSelectedBookings([]);
    } finally {
      setIsDeleting(false);
    }
  };

  const completeBooking = (bookingId) => {
    const { userId, userBookingsKey } = getUserStorageKeys();
    
    const updatedBookings = bookings.map(booking => {
      if (booking.id === bookingId) {
        return { ...booking, status: 'completed' };
      }
      return booking;
    });
    
    setBookings(updatedBookings);
    localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
    
    // Move lawyer back to available lawyers
    const completedBooking = bookings.find(b => b.id === bookingId);
    if (completedBooking && completedBooking.lawyer) {
      const availableLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
      const lawyerExists = availableLawyers.find(l => l.id === completedBooking.lawyerId);
      
      if (!lawyerExists) {
        availableLawyers.push(completedBooking.lawyer);
        localStorage.setItem('lawyers', JSON.stringify(availableLawyers));
        setLawyers(availableLawyers);
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('bookingUpdated'));
      }
    }

    // Add activity for booking completion
    addActivity({
      type: 'booking_completed',
      title: t.bookingCompleted,
      description: `Completed consultation with ${completedBooking?.lawyerName || 'lawyer'}`,
      icon: 'booking',
      priority: 'medium'
    });

    // Show rating reminder for the completed booking
    setRecentlyCompletedBooking(completedBooking);
    setShowRatingReminder(true);
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setLoading(true);
      const { userId, userBookingsKey } = getUserStorageKeys();
      
      console.log('🔄 Cancelling booking:', bookingId, 'for user:', user?.uid || user?.id);
      
      // Try to cancel in Firestore first
      const result = await bookingService.cancelBooking(bookingId, user?.uid || user?.id);
      console.log('📋 Firestore cancel result:', result);
      
      // Update local state regardless of Firestore result
      const updatedBookings = bookings.map(booking => {
        if (booking.id === bookingId) {
          return { 
            ...booking, 
            status: 'cancelled',
            cancelledAt: new Date().toISOString(),
            cancelledBy: user?.uid || user?.id
          };
        }
        return booking;
      });
      
      setBookings(updatedBookings);
      localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
      
      // Also update the general bookings storage
      const generalBookingsKey = `bookings_${userId}`;
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const updatedGeneralBookings = generalBookings.map(booking => {
        if (booking.id === bookingId) {
          return { 
            ...booking, 
            status: 'cancelled',
            cancelledAt: new Date().toISOString(),
            cancelledBy: user?.uid || user?.id
          };
        }
        return booking;
      });
      localStorage.setItem(generalBookingsKey, JSON.stringify(updatedGeneralBookings));
      
      // Move lawyer back to available lawyers if needed
      const cancelledBooking = bookings.find(b => b.id === bookingId);
      if (cancelledBooking && cancelledBooking.lawyer) {
        const availableLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
        const lawyerExists = availableLawyers.find(l => l.id === cancelledBooking.lawyerId);
        
        if (!lawyerExists) {
          availableLawyers.push(cancelledBooking.lawyer);
          localStorage.setItem('lawyers', JSON.stringify(availableLawyers));
          setLawyers(availableLawyers);
          
          // Dispatch custom event to notify other components
          window.dispatchEvent(new CustomEvent('bookingUpdated'));
        }
      }

      // Update associated chat status if exists
      if (cancelledBooking?.chatId) {
        try {
          const { chatService } = await import('../services/firebaseService');
          await chatService.updateChatStatus(cancelledBooking.chatId, 'cancelled');
          console.log('✅ Associated chat marked as cancelled');
        } catch (chatError) {
          console.error('⚠️ Could not update chat status:', chatError);
        }
      }

      if (result.success) {
        toast.success('✅ Booking cancelled successfully!');
        console.log('✅ Booking cancelled in Firestore');
      } else {
        toast.success('⚠️ Booking cancelled locally (Firestore update failed)');
        console.log('⚠️ Firestore cancellation failed:', result.error);
      }

      // Add activity for booking cancellation
      addActivity({
        type: 'booking_cancelled',
        title: t.bookingCancelled,
        description: `Cancelled consultation with ${cancelledBooking?.lawyerName || 'lawyer'}`,
        icon: 'alert',
        priority: 'medium'
      });

      // Add activity for lawyer notification
      addActivity({
        type: 'lawyer_notified',
        title: t.lawyerNotified,
        description: `${cancelledBooking?.lawyerName || 'Lawyer'} has been notified of the cancellation`,
        icon: 'message',
        priority: 'low'
      });
      
      console.log('✅ Booking cancellation process completed');
    } catch (error) {
      console.error('❌ Error cancelling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle rating submission
  const handleRatingSubmit = async (ratingData) => {
    try {
      // Import the rating service
      const { ratingService } = await import('../services/firebaseService');
      
      // Prepare rating data with user information
      const completeRatingData = {
        ...ratingData,
        clientId: user?.uid || user?.id,
        clientName: user?.displayName || user?.full_name || 'Anonymous Client',
        lawyerName: ratingData.lawyerName || selectedBooking?.lawyerName || selectedBooking?.lawyer?.name,
        consultationType: ratingData.consultationType || selectedBooking?.consultationType || selectedBooking?.type
      };

      // Submit rating to Firestore
      const result = await ratingService.submitRating(completeRatingData);
      
      if (result.success) {
        // Update local booking state to mark as rated
        const updatedBookings = bookings.map(booking => {
          if (booking.id === ratingData.bookingId) {
            return { 
              ...booking, 
              hasRated: true, 
              clientRating: ratingData.rating,
              ratingId: result.data.id,
              ratingDate: new Date().toISOString()
            };
          }
          return booking;
        });
        
        setBookings(updatedBookings);
        
        // Update localStorage for offline support
        const { userBookingsKey } = getUserStorageKeys();
        localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));
        
        // Also save to additional keys for better persistence
        const generalBookingsKey = `bookings_${user?.uid || user?.id}`;
        const additionalKey = `clientBookings_${user?.uid || user?.id}`;
        localStorage.setItem(generalBookingsKey, JSON.stringify(updatedBookings));
        localStorage.setItem(additionalKey, JSON.stringify(updatedBookings));

        // Add activity for rating submission
        addActivity({
          type: 'rating_submitted',
          title: 'Rating submitted',
          description: `Rated consultation with ${completeRatingData.lawyerName} ${ratingData.rating}/5 stars`,
          icon: 'star',
          priority: 'medium'
        });

        toast.success('Thank you for your feedback! Your rating has been submitted successfully.');
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to submit rating');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit feedback. Please try again.');
      throw error;
    }
  };

  // Open rating modal
  const openRatingModal = (booking) => {
    setSelectedBooking(booking);
    setShowRatingModal(true);
  };

  // Function to detect multiple bookings with the same lawyer
  const getMultipleBookingsWarning = () => {
    const lawyerBookings = {};
    
    bookings.forEach(booking => {
      const lawyerId = booking.lawyerId || booking.lawyer?.id;
      if (lawyerId) {
        if (!lawyerBookings[lawyerId]) {
          lawyerBookings[lawyerId] = [];
        }
        lawyerBookings[lawyerId].push(booking);
      }
    });
    
    const multipleBookings = Object.entries(lawyerBookings)
      .filter(([lawyerId, bookings]) => bookings.length > 1)
      .map(([lawyerId, bookings]) => ({
        lawyerId,
        lawyerName: bookings[0].lawyerName || bookings[0].lawyer?.name || 'Unknown Lawyer',
        count: bookings.length,
        bookings
      }));
    
    return multipleBookings;
  };
  
  const multipleBookings = getMultipleBookingsWarning();

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t.title}</h1>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
            
            {/* Offline Status Indicator */}
            {isOffline && (
              <div className="flex items-center gap-2 mt-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm w-fit">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Working offline - data may not be synced</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Selection Mode Controls - Only show when there are bookings */}
            
            {/* Selection Mode Controls - Only show when there are bookings */}
            {filteredBookings.length > 0 && (
              <>
                {isSelectionMode && selectedBookings.length > 0 && (
                  <button
                    onClick={deleteSelectedBookings}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : `${t.deleteSelected} (${selectedBookings.length})`}
                  </button>
                )}
                <button
                  onClick={toggleSelectionMode}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelectionMode 
                      ? 'bg-purple-600 text-white hover:bg-purple-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isSelectionMode ? (
                    <>
                      <CheckSquare className="w-4 h-4" />
                      {selectedBookings.length > 0 ? `${selectedBookings.length} selected` : 'Select'}
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4" />
                      Select
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Selection Mode Controls */}
        {isSelectionMode && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={selectAllBookings}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              {t.selectAll}
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={deselectAllBookings}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              {t.deselectAll}
            </button>
          </div>
        )}
      </div>

      {/* Tabs Section */}
      <div className="px-4 pb-4">
        <div className="flex bg-gray-100 rounded-xl p-1 w-full">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bookings List */}
      <div className="px-4 pb-20">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading bookings...</h3>
              <p className="text-gray-500">Please wait while we fetch your bookings</p>
            </div>
          )}
          
          {/* Bookings List */}
          {!loading && filteredBookings.length > 0 ? (
          <div className="space-y-4">
            {filteredBookings.map((booking, index) => (
              <motion.div
                key={booking.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl shadow-lg border p-4 ${
                  isSelectionMode && selectedBookings.includes(booking.id)
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-100'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Selection Checkbox */}
                  {isSelectionMode && (
                    <div className="flex-shrink-0 mt-2">
                      <button
                        onClick={() => toggleBookingSelection(booking.id)}
                        className="flex items-center justify-center w-5 h-5 rounded border-2 transition-colors"
                      >
                        {selectedBookings.includes(booking.id) ? (
                          <CheckSquare className="w-4 h-4 text-purple-600" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* Profile Picture */}
                  <div className="flex-shrink-0">
                    {booking.profilePic ? (
                      <img 
                        src={booking.profilePic} 
                        alt={booking.lawyerName} 
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                        onError={(e) => {
                          console.log('❌ Profile pic failed to load:', booking.profilePic);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200 ${booking.profilePic ? 'hidden' : ''}`}>
                      <span className="text-2xl">👨‍💼</span>
                    </div>
                  </div>

                  {/* Booking Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900">
                              {booking.lawyerName}
                            </h3>
                            {booking.id && (
                              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                                #{booking.id.slice(-6)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-purple-600 mb-1">
                            {booking.specialization}
                          </p>
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <MapPin className="w-4 h-4" />
                            <span>{booking.location}</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="font-medium">{booking.rating || 0}</span>
                            <span>•</span>
                            <span>{booking.experience || 0} years exp</span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>

                    {/* Booking Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{formatBookingDate(booking.appointmentDate || booking.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{formatBookingTime(booking.appointmentTime || booking.time)}</span>
                      </div>
                      
                      {/* Consultation Types */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-2 text-sm mb-2">
                          {getConsultationTypeIcon(booking.consultationType || booking.type || booking.consultationTypes?.[0])}
                          <span className="text-gray-600 font-medium">Consultation Type:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {booking.consultationTypes && booking.consultationTypes.length > 0 ? (
                            booking.consultationTypes.map((type, index) => (
                              <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                {getConsultationTypeName(type)}
                              </span>
                            ))
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                              {getConsultationTypeName(booking.consultationType || booking.type) || 'General Consultation'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Additional Booking Details */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          {booking.duration && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{booking.duration} min</span>
                            </div>
                          )}
                          {booking.isEmergency && (
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">Emergency</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Amount Details */}
                      <div className="col-span-2">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Consultation Fee:</span>
                            <span className="font-semibold text-gray-900">
                              ₹{booking.consultationFees || booking.price || 0}
                            </span>
                          </div>
                          {booking.fileReadingCharges > 0 && (
                            <div className="flex justify-between items-center text-sm mt-1">
                              <span className="text-gray-600">File Reading:</span>
                              <span className="font-semibold text-gray-900">
                                ₹{booking.fileReadingCharges}
                              </span>
                            </div>
                          )}
                          {booking.serviceCharge > 0 && (
                            <div className="flex justify-between items-center text-sm mt-1">
                              <span className="text-gray-600">Service Charge:</span>
                              <span className="font-semibold text-gray-900">
                                ₹{booking.serviceCharge}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-gray-200">
                            <span className="text-gray-800 font-medium">Total Amount:</span>
                            <span className="font-bold text-green-600 text-lg">
                              ₹{booking.totalAmount || booking.price || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                {/* Rating Indicator for Completed Bookings */}
                {booking.status === 'completed' && booking.hasRated && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        You rated this consultation {booking.clientRating}/5 stars
                      </span>
                    </div>
                  </div>
                )}

                {/* Rescheduled Information */}
                {booking.status === 'rescheduled' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-800">Appointment Rescheduled</span>
                    </div>
                    <div className="space-y-1 text-xs text-orange-700">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{t.originalDateTime}:</span>
                        <span className="line-through">{booking.originalDate} {booking.originalTime}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{t.newDateTime}:</span>
                        <span className="font-semibold">{booking.date} {booking.time}</span>
                      </div>
                      {booking.rescheduledBy && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{t.rescheduledBy}:</span>
                          <span className="capitalize">{booking.rescheduledBy}</span>
                        </div>
                      )}
                      {booking.rescheduledAt && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{t.rescheduledAt}:</span>
                          <span>{new Date(booking.rescheduledAt).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {/* Primary Action Buttons */}
                      <div className="flex gap-2">
                        {/* View Profile Button - Always Available */}
                        <button 
                          className="flex-1 bg-purple-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-purple-700 transition-colors"
                          onClick={() => {
                            console.log('🔍 View Profile button clicked for booking:', booking);
                            handleAction('viewProfile', booking);
                          }}
                        >
                          {t.viewProfile}
                        </button>
                        
                        {/* Status-Specific Action Buttons */}
                        {booking.status === 'upcoming' && (
                          <button 
                            className="flex-1 bg-green-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors"
                            onClick={() => handleAction('complete', booking)}
                          >
                            {t.completeBooking}
                          </button>
                        )}
                        {booking.status === 'ongoing' && (
                          <>
                            <button
                              className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-blue-700 transition-colors"
                              onClick={() => handleAction('joinCall', booking)}
                            >
                              {t.joinCall}
                            </button>
                            <button
                              className="flex-1 bg-green-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors"
                              onClick={() => handleAction('complete', booking)}
                            >
                              {t.completeBooking}
                            </button>
                          </>
                        )}
                        {booking.status === 'completed' && (
                          <>
                            {(() => {
                              console.log('🔍 Rating check for booking:', booking.id, {
                                hasRated: booking.hasRated,
                                clientRating: booking.clientRating,
                                status: booking.status
                              });
                              return !booking.hasRated ? (
                                <button 
                                  className="flex-1 bg-yellow-500 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
                                  onClick={() => openRatingModal(booking)}
                                >
                                  <ThumbsUp className="w-4 h-4" />
                                  {t.rateAndReview}
                                </button>
                              ) : (
                                <div className="flex-1">
                                  <button 
                                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-3 rounded-xl font-medium text-sm cursor-default flex items-center justify-center gap-2 shadow-sm relative"
                                    disabled
                                    title={`Rated on ${booking.ratingDate ? new Date(booking.ratingDate).toLocaleDateString() : 'recently'}`}
                                  >
                                    <Star className="w-4 h-4 fill-current" />
                                    {t.alreadyRated} ({booking.clientRating}/5)
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full flex items-center justify-center">
                                      <CheckCircle className="w-2 h-2 text-green-600 fill-current" />
                                    </div>
                                  </button>
                                  {booking.ratingDate && (
                                    <p className="text-xs text-gray-500 text-center mt-1">
                                      {t.ratedOn} {new Date(booking.ratingDate).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                        {booking.status === 'rescheduled' && (
                          <button 
                            className="flex-1 bg-green-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-green-700 transition-colors"
                            onClick={() => handleAction('complete', booking)}
                          >
                            {t.completeBooking}
                          </button>
                        )}
                        {booking.status === 'pending' && (
                          <button 
                            className="flex-1 bg-orange-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-orange-700 transition-colors"
                            onClick={() => handleAction('cancel', booking)}
                          >
                            {t.cancelBooking}
                          </button>
                        )}
                        <button
                        className="flex-1 bg-gray-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-gray-700 transition-colors"
                        onClick={async () => {
                          console.log('🔍 CHAT BUTTON CLICKED - Starting chat synchronization process');
                          console.log('🔍 Booking ID:', booking.id);
                          console.log('🔍 Lawyer ID:', booking.lawyerId);
                          
                          // Check if user is available
                          if (!user?.uid) {
                            console.error('❌ User not available for chat creation');
                            toast.error('Please log in to start a chat');
                            return;
                          }

                          try {
                            // Import chat service
                            const { chatService } = await import('../services/firebaseService');
                            
                            // First, try to find existing chat in Firestore
                            console.log('🔍 Searching for existing chat in Firestore...');
                            const existingChatsResult = await chatService.getUserChats(user.uid);
                            
                            let existingChat = null;
                            if (existingChatsResult.success && existingChatsResult.data) {
                              // Look for chat with matching booking ID or lawyer ID
                              existingChat = existingChatsResult.data.find(chat => 
                                chat.bookingId === booking.id || 
                                chat.lawyerId === booking.lawyerId ||
                                (chat.bookingId && chat.bookingId === booking.bookingId) ||
                                (chat.bookingId && chat.bookingId === booking._id)
                              );
                            }
                            
                            // If no Firestore chat found, check localStorage as fallback
                            if (!existingChat) {
                              console.log('🔍 No Firestore chat found, checking localStorage...');
                              const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
                              existingChat = storedChats.find(c => 
                                c.bookingId === booking.id || 
                                c.bookingId === booking.bookingId ||
                                c.bookingId === booking._id ||
                                c.lawyerId === booking.lawyerId
                              );
                            }
                            
                            if (existingChat) {
                              console.log('✅ Found existing chat:', existingChat);
                              
                              // Update localStorage to ensure consistency
                              const storedChats = JSON.parse(localStorage.getItem('userChats') || '[]');
                              const chatExists = storedChats.find(c => c.id === existingChat.id);
                              if (!chatExists) {
                                storedChats.push(existingChat);
                                localStorage.setItem('userChats', JSON.stringify(storedChats));
                              }
                              
                              // Navigate to existing chat
                              navigate(`/chat-conversation/${existingChat.id}`, { 
                                state: { 
                                  chat: existingChat,
                                  fromBooking: true,
                                  bookingData: booking
                                } 
                              });
                              toast.success('Opening existing chat!');
                            } else {
                              // Create new chat if none exists
                              console.log('🆕 No existing chat found, creating new one...');
                              
                              // Prepare chat data
                              const chatData = {
                                clientId: user.uid,
                                clientName: user.displayName || user.email,
                                clientEmail: user.email,
                                clientPhoto: user.photoURL || '',
                                lawyerId: booking.lawyerId,
                                lawyerName: booking.lawyerName,
                                lawyerPhoto: booking.profilePic || '',
                                caseType: booking.caseType || 'Legal Consultation',
                                bookingId: booking.id,
                                bookingDate: booking.appointmentDate || booking.date,
                                bookingTime: booking.appointmentTime || booking.time,
                                consultationType: booking.consultationType || 'Video Call',
                                status: 'active',
                                createdAt: new Date().toISOString(),
                                lastMessage: 'Chat started for booking consultation',
                                lastMessageTime: new Date().toISOString(),
                                unreadCount: 0
                              };
                              
                              console.log('🔍 Creating new chat with data:', chatData);
                              
                              // Create chat in Firestore
                              const chatResult = await chatService.createChatRoom(chatData);
                              
                              if (chatResult.success) {
                                if (chatResult.isExisting) {
                                  console.log('🔄 Using existing chat:', chatResult.data.id);
                                  toast.success('Opening existing chat conversation');
                                } else {
                                  console.log('✅ Real Firestore chat created:', chatResult.data);
                                  const realChat = {
                                    id: chatResult.data.id,
                                    chatRoomId: chatResult.data.id,
                                    ...chatData
                                  };
                                  
                                  // Add to localStorage for consistency
                                  const updatedChats = [...JSON.parse(localStorage.getItem('userChats') || '[]'), realChat];
                                  localStorage.setItem('userChats', JSON.stringify(updatedChats));
                                  
                                  // Send automated booking confirmation message only for new chats
                                  try {
                                    await chatService.sendAutomatedMessage(
                                      chatResult.data.id,
                                      'booking_confirmed',
                                      {
                                        lawyerName: booking.lawyerName,
                                        date: booking.appointmentDate || booking.date,
                                        time: booking.appointmentTime || booking.time,
                                        consultationTypes: [booking.consultationType || 'Video Call'],
                                        bookingId: booking.id,
                                        totalAmount: booking.totalAmount || 0,
                                        consultationFees: booking.consultationFees || 0,
                                        serviceCharge: 200,
                                        duration: 60
                                      }
                                    );
                                    console.log('✅ Automated booking confirmation message sent');
                                  } catch (msgError) {
                                    console.error('⚠️ Could not send automated message:', msgError);
                                  }
                                  
                                  toast.success('Chat created successfully!');
                                }
                                
                                console.log('🔍 Navigating to chat ID:', chatResult.data.id);
                                navigate(`/chat-conversation/${chatResult.data.id}`, { 
                                  state: { 
                                    chat: chatResult.data,
                                    fromBooking: true,
                                    bookingData: booking
                                  } 
                                });
                              } else {
                                console.error('❌ Failed to create Firestore chat:', chatResult.error);
                                toast.error('Failed to create chat. Please try again.');
                              }
                            }
                          } catch (error) {
                            console.error('❌ Error handling chat:', error);
                            toast.error('Failed to open chat. Please try again.');
                          }
                        }}
                        >
                          {t.chat}
                        </button>
                      </div>
                      
                      {/* Secondary Action Buttons */}
                      <div className="flex gap-2">
                        {booking.status === 'upcoming' && (
                          <button 
                            className="flex-1 bg-red-600 text-white py-2 px-3 rounded-xl font-medium text-sm hover:bg-red-700 transition-colors"
                            onClick={() => handleAction('cancel', booking)}
                          >
                            {t.cancelBooking}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          ) : !loading && (
          <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t.noBookings}
              </h3>
            <p className="text-gray-500">
              {activeTab === 'all' ? t.noBookingsDesc : `No ${activeTab} bookings`}
              </p>
            </div>
          )}
      </div>

      {/* Rating Modal */}
      {selectedBooking && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            setSelectedBooking(null);
          }}
          booking={selectedBooking}
          onSubmit={handleRatingSubmit}
        />
      )}

      {/* Rating Reminder */}
      {recentlyCompletedBooking && (
        <RatingReminder
          onRateClick={() => {
            setShowRatingReminder(false);
            openRatingModal(recentlyCompletedBooking);
          }}
          onDismiss={() => {
            setShowRatingReminder(false);
            setRecentlyCompletedBooking(null);
          }}
        />
      )}
    </div>
  );
};

export default MyBookings;